import type { FC, ReactNode } from 'react'

import { useEffect, useState } from 'react'
import { ArrowUpRight, Menu, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import OrielMark from '@/components/generals/oriel-mark'
import { NAV_LINKS } from '@/constants/landing'

const SiteHeader: FC = (): ReactNode => {
    const [scrolled, setScrolled] = useState(false)
    const [open, setOpen] = useState(false)

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24)

        onScroll()
        window.addEventListener('scroll', onScroll, { passive: true })

        return () => window.removeEventListener('scroll', onScroll)
    }, [])

    useEffect(() => {
        if (!open) return

        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setOpen(false)
        }

        document.body.style.overflow = 'hidden'
        window.addEventListener('keydown', onKey)

        return () => {
            document.body.style.overflow = ''
            window.removeEventListener('keydown', onKey)
        }
    }, [open])

    const close = () => setOpen(false)

    return (
        <header
            className={cn(
                'fixed inset-x-0 top-0 z-50 text-ink-foreground transition-[background-color,border-color,backdrop-filter] duration-500',
                scrolled || open ? 'border-b border-white/10 bg-ink/80 backdrop-blur-xl' : 'border-b border-transparent bg-transparent'
            )}
        >
            <nav
                aria-label='Main'
                className='mx-auto flex h-16 max-w-[1440px] items-center justify-between gap-6 px-5 md:h-[4.5rem] md:px-8'
            >
                <a
                    href='#hero'
                    onClick={close}
                    className='flex items-center gap-2.5 rounded-full focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ember'
                >
                    <OrielMark />

                    <span className='font-heading text-[1.65rem] leading-none tracking-tight'>
                        Oriel
                    </span>
                </a>

                <ul className='hidden items-center gap-1 lg:flex'>
                    {NAV_LINKS.map(link => (
                        <li key={link.href}>
                            <a
                                href={link.href}
                                className='rounded-full px-4 py-2 text-sm text-ink-foreground/75 transition-colors hover:bg-white/8 hover:text-ink-foreground focus-visible:outline-2 focus-visible:outline-ember'
                            >
                                {link.label}
                            </a>
                        </li>
                    ))}
                </ul>

                <div className='flex items-center gap-2'>
                    <a
                        href='#reserve'
                        onClick={close}
                        className='group inline-flex h-10 items-center gap-1.5 rounded-full bg-ink-foreground px-4 text-sm font-medium text-ink transition-colors hover:bg-ember focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ember sm:px-5'
                    >
                        Reserve
                        <ArrowUpRight className='size-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5' />
                    </a>

                    <button
                        type='button'
                        onClick={() => setOpen(value => !value)}
                        aria-expanded={open}
                        aria-controls='mobile-menu'
                        aria-label={open ? 'Close menu' : 'Open menu'}
                        className='inline-flex size-10 items-center justify-center rounded-full border border-white/15 text-ink-foreground transition-colors hover:bg-white/10 focus-visible:outline-2 focus-visible:outline-ember lg:hidden'
                    >
                        {open ? <X className='size-5' /> : <Menu className='size-5' />}
                    </button>
                </div>
            </nav>

            <div
                id='mobile-menu'
                hidden={!open}
                className='h-[calc(100svh-4rem)] overflow-y-auto border-t border-white/10 bg-ink px-5 pb-10 pt-6 lg:hidden'
            >
                <ul className='flex flex-col'>
                    {NAV_LINKS.map((link, index) => (
                        <li key={link.href} className='border-b border-white/10'>
                            <a
                                href={link.href}
                                onClick={close}
                                className='flex items-baseline justify-between py-5 font-heading text-4xl text-ink-foreground'
                            >
                                {link.label}

                                <span className='font-mono text-xs text-ink-foreground/50'>
                                    0{index + 1}
                                </span>
                            </a>
                        </li>
                    ))}
                </ul>

                <p className='mt-8 max-w-xs text-sm leading-relaxed text-ink-foreground/65'>
                    Reservations are open for first deliveries in spring 2027.
                </p>
            </div>
        </header>
    )
}

export default SiteHeader
